import "./chat.css";
import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  addDoc,
  getDoc,
  doc,
  limit,
  getDocs,
  writeBatch,
  setDoc,
  serverTimestamp,
  startAfter,
  getCountFromServer,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage, auth } from "../../services/firebase";
import {
  IoSend,
  IoDocument,
  IoClose,
  IoAttach,
  IoArrowBack,
  IoSearchOutline,
  IoChatbubbleEllipses,
  IoCheckmarkDone,
  IoCheckmark,
  IoChevronUp,
} from "react-icons/io5";
import { BsList } from "react-icons/bs";
import { Spinner } from "react-bootstrap";
import { format } from "date-fns";

const ChatWithUser = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [users, setUsers] = useState(null);
  const [messages, setMessages] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [isAdmin, setIsAdmin] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState("");
  const [unreadCounts, setUnreadCounts] = useState({});
  const [previewModal, setPreviewModal] = useState({
    show: false,
    src: "",
    type: "",
  });
  const [lastMessageDoc, setLastMessageDoc] = useState(null);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [isLoadingOlderMessages, setIsLoadingOlderMessages] = useState(false);
  const [lastMessages, setLastMessages] = useState({});
  const [totalMessageCounts, setTotalMessageCounts] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showSidebar, setShowSidebar] = useState(false);

  const currentUser = auth.currentUser;
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Initialize user admin status
  useEffect(() => {
    if (!currentUser) return;

    const userDocRef = doc(db, "users", currentUser.uid);
    getDoc(userDocRef)
      .then((docSnap) => {
        if (docSnap.exists()) {
          setIsAdmin(docSnap.data().isAdmin || false);
        }
      })
      .catch((err) => {
        console.error("Error fetching user data:", err);
        setError("Failed to load user information");
      });
  }, [currentUser]);

  // Set selected user from userId param, fetching if not in the list
  useEffect(() => {
    if (userId && users) {
      // `users` can be an empty array initially
      const userInList = users.find((user) => user.uid === userId);

      if (userInList) {
        setSelectedUser(userInList);
      } else {
        // If user not in the list, fetch their data directly
        const userDocRef = doc(db, "users", userId);
        getDoc(userDocRef)
          .then((docSnap) => {
            if (docSnap.exists()) {
              const userData = { uid: docSnap.id, ...docSnap.data() };
              // Set as selected user
              setSelectedUser(userData);
              // Add to the list for the sidebar if not already there
              setUsers((prevUsers) => {
                if (prevUsers.some((u) => u.uid === userId)) {
                  return prevUsers; // Already in list
                }
                return [...prevUsers, userData];
              });
            } else {
              setError("User not found.");
              navigate("/dashboard/chat");
            }
          })
          .catch((err) => {
            console.error("Error fetching specific user:", err);
            setError("Failed to load user data.");
          });
      }
    }
  }, [userId, users, navigate]);

  // Enhanced search with debouncing
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Filter users with enhanced search
  const filteredUsers = users
    ? users.filter(
        (user) =>
          user.name
            ?.toLowerCase()
            .includes(debouncedSearchQuery.toLowerCase()) ||
          user.email
            ?.toLowerCase()
            .includes(debouncedSearchQuery.toLowerCase()) ||
          user.displayName
            ?.toLowerCase()
            .includes(debouncedSearchQuery.toLowerCase())
      )
    : [];

  // Mark messages as read
  const markMessagesAsRead = useCallback(async () => {
    if (!selectedUser) return;

    try {
      const messagesQuery = query(
        collection(db, "messages"),
        where("receiver", "==", currentUser.uid),
        where("sender", "==", selectedUser.uid),
        where("read", "==", false)
      );

      const unreadMessages = await getDocs(messagesQuery);
      const batch = writeBatch(db);

      unreadMessages.docs.forEach((doc) => {
        batch.update(doc.ref, { read: true });
      });

      if (unreadMessages.docs.length > 0) {
        await batch.commit();
        // Update local unread counts immediately
        setUnreadCounts((prev) => {
          const newCounts = { ...prev };
          newCounts[selectedUser.uid] = 0;
          return newCounts;
        });
      }
    } catch (err) {
      console.error("Error marking messages as read:", err);
    }
  }, [selectedUser, currentUser]);

  // Real-time listener for new messages to update unread counts
  useEffect(() => {
    if (!currentUser || isAdmin === null) return;

    const unreadQuery = query(
      collection(db, "messages"),
      where("receiver", "==", currentUser.uid),
      where("read", "==", false)
    );

    const unsubscribe = onSnapshot(
      unreadQuery,
      (snapshot) => {
        const counts = {};
        snapshot.docs.forEach((doc) => {
          const message = doc.data();
          const senderId = message.sender;
          counts[senderId] = (counts[senderId] || 0) + 1;
        });
        setUnreadCounts(counts);
      },
      (error) => {
        console.error("Error listening to unread messages:", error);
      }
    );

    return () => unsubscribe();
  }, [currentUser, isAdmin]);

  // Load users based on admin status
  useEffect(() => {
    if (!currentUser || isAdmin === null) return;

    setIsLoadingUsers(true);
    let usersQuery;

    if (isAdmin) {
      // Admin sees all non-admin users
      usersQuery = query(
        collection(db, "users"),
        where("isAdmin", "==", false)
      );
    } else {
      // Regular users see only admins
      usersQuery = query(collection(db, "users"), where("isAdmin", "==", true));
    }

    const unsubscribe = onSnapshot(
      usersQuery,
      async (snapshot) => {
        try {
          const usersData = [];
          const lastMessagesData = {};

          for (const userDoc of snapshot.docs) {
            const userData = { uid: userDoc.id, ...userDoc.data() };

            // Get last message for this user
            const lastMessageQuery = query(
              collection(db, "messages"),
              where("participants", "array-contains", currentUser.uid),
              where("participants", "array-contains", userData.uid),
              orderBy("timestamp", "desc"),
              limit(1)
            );

            try {
              const lastMessageSnapshot = await getDocs(lastMessageQuery);
              if (!lastMessageSnapshot.empty) {
                const lastMessage = lastMessageSnapshot.docs[0].data();
                lastMessagesData[userData.uid] = {
                  text: lastMessage.text || "Attachment",
                  timestamp: lastMessage.timestamp,
                  sender: lastMessage.sender,
                };
                userData.lastMessage = lastMessage.text || "Attachment";
                userData.lastMessageTimestamp = lastMessage.timestamp;
              }
            } catch (err) {
              console.error("Error fetching last message:", err);
            }

            usersData.push(userData);
          }

          // Sort users by last message timestamp
          usersData.sort((a, b) => {
            const aTime = a.lastMessageTimestamp?.toMillis?.() || 0;
            const bTime = b.lastMessageTimestamp?.toMillis?.() || 0;
            return bTime - aTime;
          });

          setUsers(usersData);
          setLastMessages(lastMessagesData);
        } catch (err) {
          console.error("Error processing users:", err);
          setError("Failed to load users");
        } finally {
          setIsLoadingUsers(false);
        }
      },
      (error) => {
        console.error("Error fetching users:", error);
        setError("Failed to load users");
        setIsLoadingUsers(false);
      }
    );

    return () => unsubscribe();
  }, [currentUser, isAdmin, userId]);

  // Load messages for selected user
  const loadMessages = useCallback(
    async (loadOlder = false) => {
      if (!selectedUser || !currentUser) return;

      if (loadOlder) {
        setIsLoadingOlderMessages(true);
      } else {
        setIsLoadingMessages(true);
        setMessages([]);
        setLastMessageDoc(null);
        setHasMoreMessages(true);
      }

      try {
        let messagesQuery = query(
          collection(db, "messages"),
          where("participants", "array-contains", currentUser.uid),
          where("participants", "array-contains", selectedUser.uid),
          orderBy("timestamp", "desc"),
          limit(20)
        );

        if (loadOlder && lastMessageDoc) {
          messagesQuery = query(
            collection(db, "messages"),
            where("participants", "array-contains", currentUser.uid),
            where("participants", "array-contains", selectedUser.uid),
            orderBy("timestamp", "desc"),
            startAfter(lastMessageDoc),
            limit(20)
          );
        }

        const snapshot = await getDocs(messagesQuery);
        const messagesData = snapshot.docs.map((doc) => ({
          uid: doc.id,
          ...doc.data(),
        }));

        if (loadOlder) {
          setMessages((prev) => [...prev, ...messagesData.reverse()]);
        } else {
          setMessages(messagesData.reverse());
        }

        if (snapshot.docs.length > 0) {
          setLastMessageDoc(snapshot.docs[snapshot.docs.length - 1]);
        }

        setHasMoreMessages(snapshot.docs.length === 20);

        // Mark messages as read
        if (!loadOlder) {
          setTimeout(() => markMessagesAsRead(), 500);
        }
      } catch (err) {
        console.error("Error loading messages:", err);
        setError("Failed to load messages");
      } finally {
        setIsLoadingMessages(false);
        setIsLoadingOlderMessages(false);
      }
    },
    [selectedUser, currentUser, lastMessageDoc, markMessagesAsRead]
  );

  // Load messages when user is selected
  useEffect(() => {
    if (selectedUser) {
      loadMessages();
    }
  }, [selectedUser, loadMessages, userId]);

  // Real-time listener for new messages
  useEffect(() => {
    if (!selectedUser || !currentUser) return;

    const messagesQuery = query(
      collection(db, "messages"),
      where("participants", "array-contains", currentUser.uid),
      where("participants", "array-contains", selectedUser.uid),
      orderBy("timestamp", "desc"),
      limit(1)
    );

    const unsubscribe = onSnapshot(
      messagesQuery,
      (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          if (change.type === "added") {
            const newMessage = {
              uid: change.doc.id,
              ...change.doc.data(),
            };

            setMessages((prev) => {
              // Check if message already exists
              const exists = prev.some((msg) => msg.uid === newMessage.uid);
              if (!exists) {
                return [...prev, newMessage];
              }
              return prev;
            });

            // Mark as read if it's not from current user
            if (newMessage.sender !== currentUser.uid) {
              setTimeout(() => markMessagesAsRead(), 500);
            }
          }
        });
      },
      (error) => {
        console.error("Error listening to messages:", error);
      }
    );

    return () => unsubscribe();
  }, [selectedUser, currentUser, markMessagesAsRead]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert("File size must be less than 10MB");
        return;
      }

      setAttachment(file);
      setAttachmentPreview({
        name: file.name,
        size: formatFileSize(file.size),
        type: file.type,
      });
    }
  };

  // Handle send message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!selectedUser || (!newMessage.trim() && !attachment)) return;

    setIsSending(true);
    try {
      let attachmentUrl = null;
      let attachmentType = null;

      if (attachment) {
        const storageRef = ref(
          storage,
          `chat-attachments/${Date.now()}_${attachment.name}`
        );
        const uploadResult = await uploadBytes(storageRef, attachment);
        attachmentUrl = await getDownloadURL(uploadResult.ref);
        attachmentType = attachment.type.startsWith("image/") ? "image" : "pdf";
      }

      const messageData = {
        text: newMessage.trim(),
        sender: currentUser.uid,
        receiver: selectedUser.uid,
        participants: [currentUser.uid, selectedUser.uid],
        timestamp: serverTimestamp(),
        read: false,
        attachment: attachmentUrl,
        attachmentType: attachmentType,
      };

      await addDoc(collection(db, "messages"), messageData);

      // Update last message for both users
      const lastMessageText = newMessage.trim() || "Attachment";
      const updatePromises = [
        setDoc(
          doc(db, "users", currentUser.uid),
          {
            lastMessage: lastMessageText,
            lastMessageTimestamp: serverTimestamp(),
          },
          { merge: true }
        ),
        setDoc(
          doc(db, "users", selectedUser.uid),
          {
            lastMessage: lastMessageText,
            lastMessageTimestamp: serverTimestamp(),
          },
          { merge: true }
        ),
      ];

      await Promise.all(updatePromises);

      setNewMessage("");
      setAttachment(null);
      setAttachmentPreview(null);
    } catch (err) {
      console.error("Error sending message:", err);
      setError("Failed to send message");
    } finally {
      setIsSending(false);
    }
  };

  // Helper functions
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const removeAttachment = () => {
    setAttachment(null);
    setAttachmentPreview(null);
  };

  const selectUser = (user) => {
    setSelectedUser(user);
    setNewMessage("");
    setAttachment(null);
    setAttachmentPreview(null);
    // Update URL without page reload
    navigate(`/dashboard/chat/${user.uid}`, { replace: true });
    // Close sidebar on mobile
    if (isMobile) {
      setShowSidebar(false);
    }
  };

  const loadOlderMessages = () => {
    if (hasMoreMessages && !isLoadingOlderMessages) {
      loadMessages(true);
    }
  };

  const clearSearch = () => {
    setSearchQuery("");
  };

  const openPreview = (src, type) => {
    setPreviewModal({ show: true, src, type });
  };

  const closePreview = () => {
    setPreviewModal({ show: false, src: "", type: "" });
  };

  const goBackToChat = () => {
    navigate("/dashboard/chat");
    setSelectedUser(null);
  };

  const toggleSidebar = () => {
    setShowSidebar(!showSidebar);
  };

  const LoadingState = ({ message = "Loading..." }) => (
    <div className="loading-state">
      <Spinner animation="border" role="status" size="sm">
        <span className="visually-hidden">{message}</span>
      </Spinner>
      <p className="loading-text">{message}</p>
    </div>
  );

  const ErrorState = () => (
    <div className="error-state">
      <div className="error-content">
        <h4>Error</h4>
        <p>{error}</p>
        <button
          className="btn btn-outline-danger btn-sm"
          onClick={() => window.location.reload()}
        >
          Retry
        </button>
      </div>
    </div>
  );

  const EmptyChatState = () => (
    <div className="empty-state">
      <IoChatbubbleEllipses size={48} className="empty-icon" />
      <h5>No conversations yet</h5>
      <p>Start chatting with your clients to see messages here.</p>
    </div>
  );

  const MessageStatus = ({ message }) => {
    if (message.sender !== currentUser.uid) return null;

    return (
      <span className="message-status">
        {message.read ? (
          <IoCheckmarkDone className="text-primary" />
        ) : (
          <IoCheckmark className="text-muted" />
        )}
      </span>
    );
  };

  const formatMessageTime = (timestamp) => {
    if (!timestamp) return "";

    // Handle different timestamp types
    if (timestamp.toDate && typeof timestamp.toDate === "function") {
      // Firebase Timestamp
      return format(timestamp.toDate(), "HH:mm");
    } else if (timestamp.toMillis && typeof timestamp.toMillis === "function") {
      // Firebase Timestamp with toMillis method
      return format(new Date(timestamp.toMillis()), "HH:mm");
    } else if (typeof timestamp === "number") {
      // Unix timestamp
      return format(new Date(timestamp), "HH:mm");
    } else if (timestamp instanceof Date) {
      // JavaScript Date object
      return format(timestamp, "HH:mm");
    }

    return "";
  };

  const renderMessageContent = (message) => {
    if (message.attachment) {
      return (
        <div className="attachment">
          {message.attachmentType === "pdf" ? (
            <a 
              href={message.attachment} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="pdf-preview"
            >
              <div className="pdf-icon">
                <IoDocument size={48} color="#e74c3c" />
              </div>
              <p>Download</p>
            </a>
          ) : (
            <img
              src={message.attachment}
              alt="Attachment"
              className="attachment-image"
              onClick={() => openPreview(message.attachment, "image")}
            />
          )}
        </div>
      );
    }

    return <span>{message.text}</span>;
  };

  if (error) {
    return <ErrorState />;
  }

  return (
    <>
      <div className={`chat-sidebar ${isMobile && showSidebar ? "show" : ""}`}>
        <div className="chat-header">
          <div className="search-container">
            <IoSearchOutline className="search-icon" />
            <input
              type="text"
              className="search-input"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="clear-search" onClick={clearSearch}>
                <IoClose />
              </button>
            )}
          </div>
        </div>

        <div className="users-list">
          {isLoadingUsers ? (
            <LoadingState message="Loading users..." />
          ) : filteredUsers.length === 0 ? (
            <div className="empty-users">
              <IoChatbubbleEllipses size={48} className="empty-icon" />
              <h5>No conversations found</h5>
              <p>
                {debouncedSearchQuery
                  ? "No conversations match your search"
                  : "No client conversations yet"}
              </p>
              {debouncedSearchQuery && (
                <button className="btn btn-link" onClick={clearSearch}>
                  Clear search
                </button>
              )}
            </div>
          ) : (
            filteredUsers.map((user) => {
              // Get unread count from pre-calculated counts
              const unreadCount = unreadCounts[user.uid] || 0;
              // Get last message from local state or fallback to user data
              const lastMessage = lastMessages[user.uid] || {
                text: user.lastMessage,
                timestamp: user.lastMessageTimestamp,
                sender: null,
              };

              return (
                <div
                  key={user.uid}
                  className={`user-item ${
                    selectedUser?.uid === user.uid ? "active" : ""
                  }`}
                  onClick={() => selectUser(user)}
                >
                  <div className="user-avatar">
                    <img src={user.photoURL} alt={user.name} />
                  </div>
                  <div className="user-info">
                    <div className="user-name-row">
                      <div className="user-name">{user.name}</div>
                      {lastMessage.timestamp && (
                        <div className="user-time">
                          {formatMessageTime(lastMessage.timestamp)}
                        </div>
                      )}
                    </div>
                    {lastMessage.text && (
                      <div className="user-last-message-row">
                        <div className="user-last-message">
                          {lastMessage.text === "Attachment" ? (
                            <>
                              <IoAttach className="last-message-icon" />
                              <span>Attachment</span>
                            </>
                          ) : lastMessage.text.length > 30 ? (
                            lastMessage.text.substring(0, 30) + "..."
                          ) : (
                            lastMessage.text
                          )}
                          {lastMessage.sender === currentUser.uid && (
                            <span className="sent-indicator"> • You</span>
                          )}
                        </div>
                        {unreadCount > 0 && (
                          <div
                            className={`unread-badge ${
                              unreadCount > 9 ? "large" : ""
                            }`}
                          >
                            {unreadCount > 99 ? "99+" : unreadCount}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <div className="chat-main">
        {selectedUser ? (
          <>
            <div className="chat-header">
              <div className="header-content">
                {isMobile && (
                  <button className="back-button" onClick={toggleSidebar}>
                    <BsList size={24} />
                  </button>
                )}
                <div className="header-user-info">
                  <img
                    src={selectedUser.photoURL}
                    alt={selectedUser.name}
                    className="header-user-avatar"
                  />
                  <div className="header-user-details">
                    <h6 className="header-user-name">{selectedUser.name}</h6>
                    <p className="header-user-email">{selectedUser.email}</p>
                  </div>
                </div>
                <button
                  className="ghl-button"
                  onClick={() => {
                    if (selectedUser.ghlContactId) {
                      window.open(
                        `https://app.gohighlevel.com/v2/location/OmOFLA0kINZSjc9gGy9P/contacts/detail//${selectedUser.ghlContactId}`,
                        "_blank"
                      );
                    } else {
                      alert("GHL Contact ID not found for this user");
                    }
                  }}
                >
                  Open in GHL
                </button>
              </div>
            </div>

            <div className="messages-container" ref={messagesContainerRef}>
              {isLoadingMessages ? (
                <LoadingState message="Loading messages..." />
              ) : messages.length === 0 ? (
                <EmptyChatState />
              ) : (
                <div className="messages-list">
                  {hasMoreMessages && (
                    <div className="load-older-container">
                      <button
                        className="load-older-button"
                        onClick={loadOlderMessages}
                        disabled={isLoadingOlderMessages}
                      >
                        {isLoadingOlderMessages ? (
                          <>
                            <Spinner size="sm" animation="border" />
                            <span>Loading older messages...</span>
                          </>
                        ) : (
                          <>
                            <IoChevronUp />
                            <span>Load older messages</span>
                          </>
                        )}
                      </button>
                    </div>
                  )}
                  {messages.map((message) => (
                    <div
                      key={message.uid}
                      className={`message-item ${
                        message.sender === currentUser.uid ? "sent" : "received"
                      }`}
                    >
                      <div className="message-content">
                        {renderMessageContent(message)}
                        <div className="message-meta">
                          <span className="message-time">
                            {formatMessageTime(message.timestamp)}
                          </span>
                          <MessageStatus message={message} />
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>

            <div className="message-input-container">
              {attachmentPreview && (
                <div className="attachment-preview">
                  <div className="attachment-info">
                    <IoDocument size={24} />
                    <div className="attachment-details">
                      <p className="attachment-name">
                        {attachmentPreview.name}
                      </p>
                      <p className="attachment-size">
                        {attachmentPreview.size}
                      </p>
                    </div>
                    <button
                      className="remove-attachment"
                      onClick={removeAttachment}
                    >
                      <IoClose size={20} />
                    </button>
                  </div>
                </div>
              )}
              <form onSubmit={handleSendMessage} className="message-form">
                <div className="input-group">
                  <input
                    type="text"
                    className="message-input"
                    placeholder="Type your message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    disabled={isSending}
                    maxLength={1000}
                  />
                  <div className="input-actions">
                    <label className="attach-button">
                      <IoAttach size={22} />
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={handleFileSelect}
                        style={{ display: "none" }}
                      />
                    </label>
                    <button
                      type="submit"
                      className="send-button"
                      disabled={
                        isSending || (!newMessage.trim() && !attachment)
                      }
                    >
                      {isSending ? (
                        <Spinner size="sm" animation="border" />
                      ) : (
                        <IoSend size={24} />
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </>
        ) : (
          <div className="no-selection">
            {isMobile && (
              <button className="mobile-show-sidebar" onClick={toggleSidebar}>
                <BsList size={24} />
                <span>Select a conversation</span>
              </button>
            )}
            {!isMobile && (
              <>
                <IoChatbubbleEllipses size={64} className="no-selection-icon" />
                <h4>Select a conversation</h4>
                <p>Choose a user from the sidebar to start chatting</p>
              </>
            )}
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {previewModal.show && (
        <div className="preview-modal" onClick={closePreview}>
          <div className="preview-content" onClick={(e) => e.stopPropagation()}>
            <button className="preview-close" onClick={closePreview}>
              <IoClose size={24} />
            </button>
            {previewModal.type === "image" ? (
              <img
                src={previewModal.src}
                alt="Preview"
                className="preview-image"
              />
            ) : (
              <object
                data={previewModal.src}
                type="application/pdf"
                className="preview-pdf"
                aria-label="PDF Preview"
              >
                <p>
                  Your browser does not support PDFs. 
                  <a href={previewModal.src} download>
                    Download the PDF
                  </a>
                  .
                </p>
              </object>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWithUser;
